/**
 * A provider's own billing, client answers Q14, Q13, Q10, Q12, Q26.
 *
 * The card is the provider's, not the clinic's (Q14), so this page lives in the
 * clinic app rather than on the owner's desk. It answers two questions: am I
 * able to enrol another patient, and what am I being billed for?
 *
 * The enrolment pause (Q13) is stated prominently and in the words that matter:
 * enrolments are paused, reports are not. A provider seeing a payment warning
 * should not be left wondering whether their patients' results are stuck.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { getStripe, type StripeJs } from '../lib/stripe';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from '../components/ui';
import { IconAlert, IconCheck } from '../components/icons';

type ChargeStatus = 'PENDING' | 'PAID' | 'FAILED' | 'SKIPPED' | 'REFUNDED';
type SubStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELED' | 'PAST_DUE';

interface Installment {
  id: string;
  installment: number;
  amountCents: number;
  currency: string;
  status: ChargeStatus;
  dueAt: string;
  paidAt: string | null;
  failureReason: string | null;
}

interface Subscription {
  id: string;
  patientId: string;
  status: SubStatus;
  priceCents: number;
  currency: string;
  cycleMonths: number;
  installmentsPaid: number;
  enrolledAt: string;
  billingAnchorAt: string;
  nextChargeAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  renewalPromptedAt: string | null;
  renewalConfirmedAt: string | null;
  patient: { id: string; firstName: string; lastName: string };
  charges: Installment[];
}

interface BillingMe {
  card: { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null } | null;
  enrolmentPaused: boolean;
  enrolmentPausedReason: string | null;
  block: { code: string; message: string } | null;
  plan: { priceCents: number; currency: string; cycleMonths: number };
  stripeConfigured: boolean;
  publishableKey: string | null;
}

const SUB_STYLE: Record<SubStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  COMPLETED: 'bg-ink/5 text-ink-muted border border-ink/10',
  CANCELED: 'bg-ink/5 text-ink-muted border border-ink/10',
  PAST_DUE: 'bg-red-50 text-red-700 border border-red-200',
};

const CHARGE_STYLE: Record<ChargeStatus, string> = {
  PAID: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  PENDING: 'bg-ink/5 text-ink-muted border border-ink/10',
  FAILED: 'bg-red-50 text-red-700 border border-red-200',
  SKIPPED: 'bg-amber-50 text-amber-700 border border-amber-200',
  REFUNDED: 'bg-ink/5 text-ink-muted border border-ink/10',
};

function money(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((cents ?? 0) / 100);
}

const day = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

export function BillingPage() {
  const qc = useQueryClient();
  const [editingCard, setEditingCard] = useState(false);

  const me = useQuery({ queryKey: ['billing', 'me'], queryFn: () => api.get<BillingMe>('/billing/me') });
  const subs = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: () => api.get<{ subscriptions: Subscription[] }>('/billing/subscriptions'),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['billing'] });
  };

  const removeCard = useMutation({
    mutationFn: () => api.delete('/billing/me/payment-method'),
    onSuccess: invalidate,
  });

  const data = me.data;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Billing"
        subtitle="Your payment method, and the monitoring cycles you are billed for."
      />

      {/* Q13, say exactly what the pause does, and what it does not do. */}
      {data?.enrolmentPaused && (
        <div className="flex items-start gap-2 rounded-sm2 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <span className="mt-0.5 shrink-0">
            <IconAlert size={16} />
          </span>
          <p>
            <strong>New patient enrolments are paused.</strong>{' '}
            {data.enrolmentPausedReason ?? 'A payment did not go through.'} Reports for patients
            already enrolled are unaffected and continue to be produced and delivered, adding a
            working card lifts the pause immediately.
          </p>
        </div>
      )}

      {data && !data.stripeConfigured && (
        <div className="flex items-start gap-2 rounded-sm2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="mt-0.5 shrink-0">
            <IconAlert size={16} />
          </span>
          <p>
            <strong>Payments are not switched on yet.</strong> Cycles are being tracked and
            installments recorded, but no card is charged until MCOS adds its live payment keys.
          </p>
        </div>
      )}

      {/* --- the plan + the card --- */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Your plan">
          {me.isLoading ? (
            <LoadingRows rows={2} />
          ) : data ? (
            <>
              <p className="text-2xl font-semibold text-ink">
                {money(data.plan.priceCents, data.plan.currency)}
                <span className="ml-1 text-sm font-normal text-ink-muted">
                  per patient, per month
                </span>
              </p>
              <p className="mt-1.5 text-sm text-ink-muted">
                Each patient you enrol runs a {data.plan.cycleMonths}-month monitoring cycle -{' '}
                {data.plan.cycleMonths} monthly charges, then you are asked whether to renew. The
                first charge falls at the end of the week you add them.
              </p>
              <p className="mt-2 text-[11px] text-ink-muted">
                Patients are never charged by MCOS. Anything a patient pays, they pay you.
              </p>
            </>
          ) : null}
        </Card>

        <Card
          title="Payment method"
          action={
            data?.card && !editingCard ? (
              <Button size="sm" variant="secondary" onClick={() => setEditingCard(true)}>
                Replace
              </Button>
            ) : undefined
          }
        >
          {me.isLoading ? (
            <LoadingRows rows={2} />
          ) : editingCard || !data?.card ? (
            data?.stripeConfigured && data.publishableKey ? (
              <CardForm
                publishableKey={data.publishableKey}
                onSaved={() => {
                  setEditingCard(false);
                  invalidate();
                }}
                onCancel={data.card ? () => setEditingCard(false) : undefined}
              />
            ) : (
              <p className="text-sm text-ink-muted">
                A card cannot be added until MCOS switches payments on.
              </p>
            )
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-emerald-600">
                  <IconCheck size={16} />
                </span>
                <span className="text-sm text-ink">
                  <strong className="capitalize">{data.card.brand ?? 'Card'}</strong> ending{' '}
                  {data.card.last4}
                  {data.card.expMonth && data.card.expYear
                    ? ` · expires ${String(data.card.expMonth).padStart(2, '0')}/${data.card.expYear}`
                    : ''}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                loading={removeCard.isPending}
                onClick={() => removeCard.mutate()}
              >
                Remove
              </Button>
              <p className="text-[11px] text-ink-muted">
                Removing your card stops you from enrolling new patients.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* --- the cycles --- */}
      <Card title="Monitoring cycles" padded={false}>
        {subs.isLoading ? (
          <div className="p-5">
            <LoadingRows rows={4} />
          </div>
        ) : subs.isError ? (
          <div className="p-5">
            <ErrorState error={subs.error} retry={() => void subs.refetch()} />
          </div>
        ) : (subs.data?.subscriptions.length ?? 0) === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No monitoring cycles yet"
              body="Enrol a patient in the six-month programme when you add them, and their cycle will appear here."
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>Status</Th>
                <Th>Progress</Th>
                <Th>Next charge</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {subs.data?.subscriptions.map((s) => (
                <SubscriptionRow key={s.id} subscription={s} onChanged={invalidate} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function SubscriptionRow({
  subscription,
  onChanged,
}: {
  subscription: Subscription;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  const cancel = useMutation({
    mutationFn: () => api.post(`/billing/subscriptions/${subscription.id}/cancel`, {}),
    onSuccess: onChanged,
  });
  const renew = useMutation({
    mutationFn: () => api.post(`/billing/subscriptions/${subscription.id}/confirm-renewal`, {}),
    onSuccess: onChanged,
  });

  const settled = subscription.charges.filter(
    (c) => c.status === 'PAID' || c.status === 'SKIPPED',
  ).length;
  const renewalDue =
    Boolean(subscription.renewalPromptedAt) && !subscription.renewalConfirmedAt;

  return (
    <>
      <tr className={open ? 'bg-ink/5' : undefined}>
        <Td>
          <div className="font-medium text-ink">
            {subscription.patient.firstName} {subscription.patient.lastName}
          </div>
          <div className="text-xs text-ink-muted">
            enrolled {day(subscription.enrolledAt)} · {money(subscription.priceCents, subscription.currency)}/mo
          </div>
        </Td>
        <Td>
          <Pill className={SUB_STYLE[subscription.status]}>
            {subscription.status.toLowerCase().replace('_', ' ')}
          </Pill>
          {renewalDue && (
            <div className="mt-1 text-[11px] text-amber-700">renewal decision due</div>
          )}
        </Td>
        <Td>
          <span className="tabular-nums text-ink">
            {settled} / {subscription.cycleMonths}
          </span>
          <div className="text-[11px] text-ink-muted">months billed</div>
        </Td>
        <Td>{day(subscription.nextChargeAt)}</Td>
        <Td align="right">
          <div className="flex justify-end gap-1.5">
            {renewalDue && (
              <Button size="sm" loading={renew.isPending} onClick={() => renew.mutate()}>
                Renew
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setOpen(!open)}>
              {open ? 'Close' : 'Detail'}
            </Button>
          </div>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-ink/5 px-5 py-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-ink-muted">
                    <th className="py-1.5 pr-4">Month</th>
                    <th className="py-1.5 pr-4">Due</th>
                    <th className="py-1.5 pr-4">Amount</th>
                    <th className="py-1.5 pr-4">Status</th>
                    <th className="py-1.5">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {subscription.charges.map((c) => (
                    <tr key={c.id} className="border-t border-ink/10">
                      <td className="py-1.5 pr-4 tabular-nums">{c.installment}</td>
                      <td className="py-1.5 pr-4">{day(c.dueAt)}</td>
                      <td className="py-1.5 pr-4 tabular-nums">
                        {money(c.amountCents, c.currency)}
                      </td>
                      <td className="py-1.5 pr-4">
                        <Pill className={CHARGE_STYLE[c.status]}>{c.status.toLowerCase()}</Pill>
                      </td>
                      <td className="py-1.5 text-xs text-ink-muted">{c.failureReason ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {subscription.status !== 'CANCELED' && subscription.status !== 'COMPLETED' && (
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ink/10 pt-3">
                <Button
                  size="sm"
                  variant="danger"
                  loading={cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  Cancel this cycle
                </Button>
                <span className="text-[11px] text-ink-muted">
                  Billing stops immediately for the rest of the cycle. Reports already produced are
                  unaffected.
                </span>
              </div>
            )}
            {subscription.cancelReason && (
              <p className="mt-2 text-xs text-ink-muted">
                Cancelled {day(subscription.canceledAt)}, {subscription.cancelReason}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Stripe Elements card capture. The card never reaches an MCOS server. */
function CardForm({
  publishableKey,
  onSaved,
  onCancel,
}: {
  publishableKey: string;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeJs | null>(null);
  type CardElement = ReturnType<ReturnType<StripeJs['elements']>['create']>;
  const cardRef = useRef<CardElement | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cardEl: { unmount: () => void } | null = null;
    getStripe(publishableKey)
      .then((stripe) => {
        if (cancelled || !mountRef.current) return;
        stripeRef.current = stripe;
        const el = stripe.elements().create('card', { style: { base: { fontSize: '14px' } } });
        el.mount(mountRef.current);
        el.on('change', (e) => setError(e.error?.message ?? null));
        cardRef.current = el;
        cardEl = el;
        setReady(true);
      })
      .catch(() => setError('Could not load Stripe.js. Check the connection and try again.'));
    return () => {
      cancelled = true;
      try {
        cardEl?.unmount();
      } catch {
        /* ignore */
      }
    };
  }, [publishableKey]);

  async function submit() {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return;
    setSaving(true);
    setError(null);
    try {
      const { clientSecret } = await api.post<{ clientSecret: string }>('/billing/me/setup-intent');
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
      if (result.error) {
        setError(result.error.message ?? 'The card could not be saved.');
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (!pmId) {
        setError('Stripe did not return a payment method.');
        return;
      }
      await api.post('/billing/me/payment-method', { paymentMethodId: pmId });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the card.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div ref={mountRef} className="rounded-sm2 border border-ink/10 bg-white px-3 py-2.5" />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={submit} loading={saving} disabled={!ready}>
          Save card
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        Card details are sent directly to Stripe and never stored on MCOS servers.
      </p>
    </div>
  );
}
